<template>
  <div class="tdf-wrap w-100" ref="wrap">
    <!-- trigger button -->
    <div class="tdf-btn-group" :class="{ active: hasValue }">
      <button
          class="btn btn-sm btn-outline-secondary tdf-btn mb-0"
          type="button"
          @click="isOpen = !isOpen"
      >
        <i class="far fa-calendar-alt me-1"></i>
        <span v-if="hasValue" class="tdf-label">{{ label }}</span>
        <span v-else class="tdf-label text-muted">—</span>
      </button>
      <button
          v-if="hasValue"
          class="btn btn-sm btn-outline-secondary tdf-clear mb-0"
          type="button"
          @click="clear"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- dropdown panel -->
    <div v-if="isOpen" class="tdf-panel shadow-sm border rounded bg-white p-2">
      <div class="d-flex align-items-center gap-2 flex-nowrap">
        <select
            class="form-select form-select-sm"
            style="width: 62px; flex-shrink: 0;"
            v-model="operator"
            @change="onOperatorChange"
        >
          <option value="between">↔</option>
          <option value="<">&lt;</option>
          <option value="<=">≤</option>
          <option value=">">></option>
          <option value=">=">≥</option>
        </select>

        <ElDatePicker
            v-if="operator === 'between'"
            v-model="rangeValue"
            type="daterange"
            size="small"
            unlink-panels
            :clearable="false"
            :range-separator="$t('TableDateFilter.to')"
            :start-placeholder="$t('TableDateFilter.from')"
            :end-placeholder="$t('TableDateFilter.to')"
            @change="onRangeChange"
        />
        <ElDatePicker
            v-else
            v-model="singleValue"
            type="date"
            size="small"
            :clearable="false"
            :placeholder="$t('TableDateFilter.pickDate')"
            style="width: 150px;"
            @change="onSingleChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ElDatePicker } from 'element-plus'

export default {
  name: 'TableDateFilter',
  components: { ElDatePicker },
  emits: ['change'],
  data() {
    return {
      isOpen: false,
      operator: 'between',
      rangeValue: null,
      singleValue: null,
    }
  },
  computed: {
    hasValue() {
      if (this.operator === 'between') return !!this.rangeValue
      return !!this.singleValue
    },
    label() {
      const fmt = d => {
        const date = new Date(d)
        return date.getDate() + '.' + String(date.getMonth() + 1).padStart(2, '0')
      }
      if (this.operator === 'between' && this.rangeValue) {
        return fmt(this.rangeValue[0]) + ' — ' + fmt(this.rangeValue[1])
      }
      if (this.singleValue) {
        const sym = { '<': '<', '<=': '≤', '>': '>', '>=': '≥' }[this.operator] ?? this.operator
        return sym + ' ' + fmt(this.singleValue)
      }
      return '—'
    }
  },
  methods: {
    onOperatorChange() {
      this.rangeValue = null
      this.singleValue = null
      this.$emit('change', null)
    },
    onRangeChange(val) {
      if (!val) {
        this.$emit('change', null)
        return
      }
      this.$emit('change', [val[0].toISOString(), val[1].toISOString()])
    },
    onSingleChange(val) {
      if (!val) {
        this.$emit('change', null)
        return
      }
      this.$emit('change', this.operator + ' ' + val.toISOString())
    },
    clear() {
      this.rangeValue = null
      this.singleValue = null
      this.isOpen = false
      this.$emit('change', null)
    },
    onClickOutside(e) {
      if (this.$refs.wrap && !this.$refs.wrap.contains(e.target)) {
        // don't close if click is inside an el-picker popper (rendered outside DOM tree)
        if (e.target.closest('.el-picker-panel, .el-date-range-picker')) return
        this.isOpen = false
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside)
  }
}
</script>

<style scoped>
.tdf-wrap {
  position: relative;
  display: inline-block;
}
.tdf-btn-group {
  display: flex;
  width: 100%;
}
.tdf-btn-group > .btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;
}
.tdf-btn-group > .btn:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.tdf-btn {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  line-height: 1.5;
  white-space: nowrap;
  border-color: var(--ui-border-3, #d2d6da);
  color: var(--ui-text-3, #7b809a);
  background: transparent;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tdf-btn-group.active .tdf-btn,
.tdf-btn-group.active .tdf-clear {
  border-color: var(--ui-border-3, #adb5bd);
  color: var(--ui-text-1, #344767);
}
.tdf-label {
  font-size: 0.72rem;
}
.tdf-clear {
  flex: 0 0 auto;
  font-size: 0.7rem;
  padding: 0.25rem 0.45rem;
  line-height: 1.5;
  border-color: var(--ui-border-3, #d2d6da);
  color: var(--ui-text-3, #7b809a);
  background: transparent;
}
.tdf-clear:hover {
  color: var(--ui-text-1, #344767);
}
.tdf-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1050;
  white-space: nowrap;
  background: var(--ui-bg-card, #fff);
  border-color: var(--ui-border, #dee2e6) !important;
}
</style>
